/**
 * src/components/layout/CommandPalette.tsx
 * ------------------------------------------
 * Global search + command palette, opened by the top-bar search button or
 * Cmd/Ctrl+K.
 *
 * Groups:
 *   - Conversations: live search by session ID (GET /conversations?search=)
 *   - Knowledge Base: live search across ingested documents
 *   - Navigate: jump to any view
 *
 * It used to render the Navigate group only, so typing a session ID or a
 * document name returned "No results found" — the palette opened fine, but it
 * couldn't actually search anything, which is what made it read as broken.
 *
 * Remote results are fetched with a debounced query and cached briefly.
 * `shouldFilter={false}` hands match ranking to the server for remote rows;
 * navigation rows are filtered locally against the same input.
 *
 * The palette is rendered as a dialog so it traps focus and is dismissed on
 * Escape or backdrop click.
 *
 * Depends on: cmdk, lucide-react, react-router-dom, @tanstack/react-query
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Command } from 'cmdk'
import {
  LayoutDashboard, Radio, MessageSquare, Bell, ScrollText,
  BookOpen, GitBranch, FileText, Search, Users, Cpu, Paintbrush, Mic,
  X,
} from 'lucide-react'
import { ROUTES } from '@shared/constants'
import type { Conversation, KnowledgeDocument, PaginatedResponse } from '@shared/types'
import apiClient from '@/api/client'
import { listDocuments } from '@/api/knowledge'
import { useDebounce } from '@/hooks/useDebounce'
import { cn } from '@/lib/utils'

interface CommandPaletteProps {
  open: boolean
  onClose: () => void
}

interface CommandItem {
  id: string
  label: string
  group: string
  icon: React.ComponentType<{ className?: string }>
  action: () => void
  keywords?: string[]
}

const MIN_QUERY_LENGTH = 2
const REMOTE_LIMIT = 5

async function searchConversations(search: string): Promise<Conversation[]> {
  const { data } = await apiClient.get<PaginatedResponse<Conversation>>('/conversations', {
    params: { search, page_size: REMOTE_LIMIT },
  })
  return data.items ?? []
}

const itemClass = cn(
  'flex cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-sm',
  'aria-selected:bg-accent aria-selected:text-foreground',
)

export function CommandPalette({ open, onClose }: CommandPaletteProps) {
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const debouncedQuery = useDebounce(query.trim(), 250)
  const canSearch = open && debouncedQuery.length >= MIN_QUERY_LENGTH

  const nav = useCallback(
    (to: string) => {
      navigate(to)
      onClose()
    },
    [navigate, onClose],
  )

  // Reset the query each time the palette opens so it never reopens mid-search.
  useEffect(() => {
    if (open) setQuery('')
  }, [open])

  const navigationItems: CommandItem[] = useMemo(() => [
    { id: 'nav-dashboard', label: 'Go to Dashboard', group: 'Navigate', icon: LayoutDashboard, action: () => nav(ROUTES.DASHBOARD), keywords: ['home', 'kpi', 'metrics'] },
    { id: 'nav-livewall', label: 'Go to Live Wall', group: 'Navigate', icon: Radio, action: () => nav(ROUTES.LIVE_WALL), keywords: ['realtime', 'active'] },
    { id: 'nav-conversations', label: 'Go to Conversations', group: 'Navigate', icon: MessageSquare, action: () => nav(ROUTES.CONVERSATIONS), keywords: ['chats', 'sessions', 'transcripts'] },
    { id: 'nav-alerts', label: 'Go to Alerts', group: 'Navigate', icon: Bell, action: () => nav(ROUTES.ALERTS), keywords: ['escalations', 'notifications'] },
    { id: 'nav-logs', label: 'Go to Logs', group: 'Navigate', icon: ScrollText, action: () => nav(ROUTES.LOGS) },
    { id: 'nav-kb', label: 'Go to Knowledge Base', group: 'Navigate', icon: BookOpen, action: () => nav(ROUTES.KNOWLEDGE_BASE), keywords: ['documents', 'rag', 'upload'] },
    { id: 'nav-flows', label: 'Go to Flows', group: 'Navigate', icon: GitBranch, action: () => nav(ROUTES.FLOWS) },
    { id: 'nav-prompts', label: 'Go to Prompts', group: 'Navigate', icon: FileText, action: () => nav(ROUTES.PROMPTS) },
    { id: 'nav-retrieval', label: 'Go to Retrieval', group: 'Navigate', icon: Search, action: () => nav(ROUTES.RETRIEVAL) },
    { id: 'nav-crm', label: 'Go to CRM', group: 'Navigate', icon: Users, action: () => nav(ROUTES.CRM), keywords: ['customers', 'roles', 'users'] },
    { id: 'nav-llm', label: 'Go to LLM Config', group: 'Navigate', icon: Cpu, action: () => nav(ROUTES.LLM_CONFIG), keywords: ['model', 'provider', 'openai', 'anthropic'] },
    { id: 'nav-voice', label: 'Go to Voice & Style', group: 'Navigate', icon: Mic, action: () => nav(ROUTES.VOICE_STYLE) },
    { id: 'nav-appearance', label: 'Go to Appearance', group: 'Navigate', icon: Paintbrush, action: () => nav(ROUTES.APPEARANCE), keywords: ['branding', 'theme', 'logo'] },
  ], [nav])

  // Server-side ranking is off, so filter navigation rows here.
  const visibleNavItems = useMemo(() => {
    const q = debouncedQuery.toLowerCase()
    if (!q) return navigationItems
    return navigationItems.filter((item) =>
      [item.label, ...(item.keywords ?? [])].join(' ').toLowerCase().includes(q),
    )
  }, [navigationItems, debouncedQuery])

  const conversations = useQuery({
    queryKey: ['palette', 'conversations', debouncedQuery],
    queryFn: () => searchConversations(debouncedQuery),
    enabled: canSearch,
    staleTime: 30_000,
    retry: false,
  })

  const documents = useQuery({
    queryKey: ['palette', 'documents', debouncedQuery],
    queryFn: () => listDocuments({ page: 1, page_size: 50 }),
    enabled: canSearch,
    staleTime: 60_000,
    retry: false,
  })

  // The documents endpoint has no server-side text filter, so match locally
  // over one page rather than pretending the whole corpus was searched.
  const matchedDocuments: KnowledgeDocument[] = useMemo(() => {
    const q = debouncedQuery.toLowerCase()
    if (!q) return []
    return (documents.data?.items ?? [])
      .filter((doc) => doc.name?.toLowerCase().includes(q))
      .slice(0, REMOTE_LIMIT)
  }, [documents.data, debouncedQuery])

  // Close on Escape
  useEffect(() => {
    if (!open) return
    function handleEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleEsc)
    return () => window.removeEventListener('keydown', handleEsc)
  }, [open, onClose])

  if (!open) return null

  const searching = canSearch && (conversations.isFetching || documents.isFetching)
  const hasResults =
    visibleNavItems.length > 0 ||
    (conversations.data?.length ?? 0) > 0 ||
    matchedDocuments.length > 0

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh]">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-background/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      <div className="relative z-10 w-full max-w-lg rounded-xl border border-border bg-popover shadow-2xl">
        <Command
          shouldFilter={false}
          className="[&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium"
        >
          <div className="flex items-center border-b border-border px-3">
            <Search className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
            <Command.Input
              autoFocus
              value={query}
              onValueChange={setQuery}
              placeholder="Search conversations, documents and commands…"
              className="flex-1 bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
            />
            <button onClick={onClose} className="p-1 text-muted-foreground hover:text-foreground">
              <X className="h-4 w-4" />
            </button>
          </div>

          <Command.List className="max-h-80 overflow-y-auto p-1">
            {!hasResults && !searching && (
              <Command.Empty className="py-6 text-center text-sm text-muted-foreground">
                No results found
              </Command.Empty>
            )}

            {searching && (
              <div className="py-3 text-center text-xs text-muted-foreground">Searching…</div>
            )}

            {(conversations.data?.length ?? 0) > 0 && (
              <Command.Group heading="Conversations">
                {conversations.data!.map((conversation) => (
                  <Command.Item
                    key={conversation.id}
                    value={`conversation-${conversation.id}`}
                    onSelect={() => nav(ROUTES.CONVERSATIONS)}
                    className={itemClass}
                  >
                    <MessageSquare className="h-4 w-4 text-muted-foreground" />
                    <span className="truncate">
                      {conversation.session_id || conversation.id}
                    </span>
                    {conversation.status && (
                      <span className="ml-auto text-xs text-muted-foreground">
                        {conversation.status}
                      </span>
                    )}
                  </Command.Item>
                ))}
              </Command.Group>
            )}

            {matchedDocuments.length > 0 && (
              <Command.Group heading="Knowledge Base">
                {matchedDocuments.map((doc) => (
                  <Command.Item
                    key={doc.id}
                    value={`document-${doc.id}`}
                    onSelect={() => nav(ROUTES.KNOWLEDGE_BASE)}
                    className={itemClass}
                  >
                    <BookOpen className="h-4 w-4 text-muted-foreground" />
                    <span className="truncate">{doc.name}</span>
                  </Command.Item>
                ))}
              </Command.Group>
            )}

            {visibleNavItems.length > 0 && (
              <Command.Group heading="Navigate">
                {visibleNavItems.map((item) => (
                  <Command.Item
                    key={item.id}
                    value={item.id}
                    onSelect={item.action}
                    className={itemClass}
                  >
                    <item.icon className="h-4 w-4 text-muted-foreground" />
                    {item.label}
                  </Command.Item>
                ))}
              </Command.Group>
            )}
          </Command.List>
        </Command>
      </div>
    </div>
  )
}
